import { createStore } from 'vuex'
import { menuModule } from './modules/menu'
import { useAuth } from '@/composables/useAuth'
import globalModule from './modules/global'
import loaderModule from './modules/loader'
import notificationModule from './modules/notification'
import { RootState } from './types'
import { auth, database } from '@/firebase/config'
import { ref, set } from 'firebase/database'
import { EGender } from '@shared/types/enums'
import LogRocket from 'logrocket'

const store = createStore<RootState>({
	state: {
		auth: null,
		user: null,
		certificates: [],
		degrees: [],
		loading: false,
		error: null,
		testMode: false,
		global: {
			testMode: false,
			user: {
				me: {
					userData: {
						institution: '',
						testMode: false,
						base64Cer: ''
					},
					role: undefined,
					institutions: {},
					cer: {
						certificadoResponsable: '',
						noCertificadoResponsable: '',
						pem: '',
						x509: {
							extensions: {
								basicConstraints: {
									cA: false,
									critical: false,
									id: '',
									name: '',
									value: ''
								},
								extKeyUsage: {
									clientAuth: false,
									critical: false,
									emailProtection: false,
									id: '',
									name: '',
									value: ''
								},
								keyUsage: {
									cRLSign: false,
									critical: false,
									dataEncipherment: false,
									decipherOnly: false,
									digitalSignature: false,
									encipherOnly: false,
									id: '',
									keyAgreement: false,
									keyCertSign: false,
									keyEncipherment: false,
									name: '',
									nonRepudiation: false,
									value: ''
								},
								nsCertType: {
									client: false,
									critical: false,
									email: false,
									emailCA: false,
									id: '',
									name: '',
									objCA: false,
									objsign: false,
									reserved: false,
									server: false,
									sslCA: false,
									value: ''
								}
							},
							fingerPrint: '',
							issuer: [],
							notAfter: '',
							notBefore: '',
							publicKey: {
								bitSize: 0,
								fingerPrint: '',
								pem: ''
							},
							serial: '',
							siginfo: {
								algorithmOid: ''
							},
							signature: '',
							subject: {
								'2-5-4-41': '',
								'2-5-4-45': '',
								commonName: '',
								countryName: '',
								emailAddress: '',
								organizationName: '',
								serialNumber: ''
							},
							subjectHash: '',
							version: 0
						}
					},
					curp: '',
					email: '',
					nombre: '',
					primerApellido: '',
					segundoApellido: '',
					phone: '',
					genere: EGender.MALE
				}
			},
			institutions: {},
			selectedInstitution: {
				CCT: '',
				hasCertisep: false,
				Carrera: {},
				certisep: {},
				Campus: '',
				idNombreInstitucion: '',
				cveInstitution: '',
				Responsable: {},
				idCampus: {},
				nombreInstitucion: '',
				logo128: ''
			},
			cveCarreraToIdCarrera: {}
		},
		loader: {
			isUploading: false,
			progress: 0,
			error: null,
			uploadTimeout: null,
			uploadStartTime: null,
			isLoading: false,
			message: ''
		},
		notification: {
			successMessage: null,
			showSuccessModal: false,
			errorMessage: null,
			showErrorModal: false
		},
		contractDate: {
			fechaInicioContrato: null,
			fechaFinContrato: null
		},
		menu: {
			isOpen: false
		}
	},

	mutations: {
		SET_USER(state, user) {
			state.user = user
			if (user && user.uid) {
				LogRocket.identify(user.uid, {
					name: user.name,
					email: user.email,
					institution: user.institution,
					mode: user.testMode ? 'test' : 'production'
				})
			}
		},
		SET_CERTIFICATES(state, certificates) {
			state.certificates = certificates
		},
		SET_DEGREES(state, degrees) {
			state.degrees = degrees
		},
		SET_LOADING(state, loading) {
			state.loading = loading
		},
		SET_ERROR(state, error) {
			state.error = error
		},
		setTestMode(state, testMode) {
			state.testMode = testMode
		},
		SET_CONTRACT_DATE(state, contractDate) {
			state.contractDate = contractDate
		}
	},

	actions: {
		async login({ commit }, { email, password }) {
			try {
				const { login } = useAuth()
				const user = await login(email, password)
				commit('SET_USER', user)
				return user
			} catch (error) {
				commit('SET_ERROR', error)
				throw error
			}
		},

		async logout({ commit }) {
			try {
				const { logout } = useAuth()
				await logout()
				commit('SET_USER', null)
			} catch (error) {
				commit('SET_ERROR', error)
				throw error
			}
		},

		async fetchCertificates({ commit }) {
			try {
				commit('SET_LOADING', true)
				// TODO: Implement fetch certificates logic
				commit('SET_CERTIFICATES', [])
			} catch (error) {
				commit('SET_ERROR', error)
			} finally {
				commit('SET_LOADING', false)
			}
		},

		async toggleTestMode({ commit, state }) {
			try {
				const uid = auth.currentUser?.uid
				if (!uid) throw new Error('No user logged in')

				const newTestMode = !state.testMode
				await set(ref(database, `users/${uid}/userData/testMode`), newTestMode)

				// Actualizar todos los estados de testMode
				commit('setTestMode', newTestMode)
				commit('global/SET_TEST_MODE', newTestMode)

				// Guardar en localStorage para persistencia
				localStorage.setItem('testMode', String(newTestMode))
			} catch (error) {
				commit('SET_ERROR', error)
				throw error
			}
		},
		async cleanUploadDegree({ commit }) {
			try {
				const uid = auth.currentUser?.uid
				if (!uid) throw new Error('No user logged in')
				await set(ref(database, `users/${uid}/userData/uploadDegree`), null)
			} catch (error) {
				commit('SET_ERROR', error)
				throw error
			}
		},
		async cleanUploadCertificates({ commit }) {
			try {
				const uid = auth.currentUser?.uid
				if (!uid) throw new Error('No user logged in')
				await set(ref(database, `users/${uid}/userData/uploadCertificate`), null)
			} catch (error) {
				commit('SET_ERROR', error)
				throw error
			}
		},
		async updateCertificate({ commit }, { certificate }) {
			try {
				const uid = auth.currentUser?.uid
				if (!uid) throw new Error('No user logged in')
				const newCertificate = certificate
				await set(ref(database, `users/${uid}/userData/base64Cer`), newCertificate)
			} catch (error) {
				commit('SET_ERROR', error)
				throw error
			}
		},
		async updateConfiguration({ commit }, { configuration, institutionId }) {
			try {
				const uid = auth.currentUser?.uid
				if (!uid) throw new Error('No user logged in')
				const newConfiguration = configuration
				await set(ref(database, `institutions/${institutionId}/config`), newConfiguration)
			} catch (error) {
				commit('SET_ERROR', error)
				throw error
			}
		}
	},

	getters: {
		isAuthenticated: state => Boolean(state.user),
		getCertificates: state => state.certificates,
		getDegrees: state => state.degrees,
		isLoading: state => state.loading,
		getError: state => state.error,
		isLoggedIn: state => Boolean(state.user),
		isTestMode: state => state.testMode,
		getContractDate: state => state.contractDate
	},

	modules: {
		menu: menuModule,
		global: globalModule,
		loader: loaderModule,
		notification: notificationModule
	}
})

export default store

export function useStore() {
	return store
}
